// Structured label color helpers (BQ-14).
//
// The codebase historically encoded custom label colors in a delimited
// string of the form `bg:#hex|text:#hex|border:#hex`. That parse logic was
// duplicated across at least four sites. This module centralizes:
//   - parseLabelColor: parse the legacy encoding into a structured value
//   - getEntityColorClass: deterministic label -> color-class mapping
//     (moved verbatim from fileParser.ts)

export interface LabelColor {
  bg: string;
  text: string;
  border: string;
}

const DEFAULT_LABEL_COLOR: LabelColor = {
  bg: '#f3f4f6', // gray-100
  text: '#1f2937', // gray-800
  border: '#9ca3af' // gray-400
};

/**
 * Parse the legacy `bg:#hex|text:#hex|border:#hex` encoding into a
 * structured LabelColor. Falls back to a sensible neutral default if the
 * input is undefined or unparseable.
 *
 * Also accepts the legacy single-color formats (raw `#hex` / `rgb(...)`),
 * deriving a translucent background and using the value for text + border.
 */
export function parseLabelColor(s: string | undefined): LabelColor {
  if (!s) return { ...DEFAULT_LABEL_COLOR };

  if (s.includes('|')) {
    const parts = s.split('|');
    const bg = (parts[0] || '').replace('bg:', '');
    const text = (parts[1] || '').replace('text:', '');
    const border = parts[2] ? parts[2].replace('border:', '') : text;
    if (!bg || !text) return { ...DEFAULT_LABEL_COLOR };
    return { bg, text, border: border || text };
  }

  if (s.startsWith('#') || s.startsWith('rgb')) {
    // Legacy single-color encoding: derive a translucent background.
    return { bg: s + '20', text: s, border: s };
  }

  // Tailwind class strings or anything else we can't parse → default.
  return { ...DEFAULT_LABEL_COLOR };
}

export function getEntityColorClass(label: string, customColors?: Record<string, string>): string {
  // Check for custom color first
  if (customColors && customColors[label]) {
    return customColors[label];
  }

  // Normalize: lowercase and remove non-alphanumeric characters
  const normalizedLabel = label.toLowerCase().replace(/[^a-z0-9]/g, '');

  // Check for semantic matches (regardless of BIO prefix)
  // Disease/Disorder - RED
  if (normalizedLabel.includes('disease') || normalizedLabel.includes('disorder') ||
      normalizedLabel.includes('condition') || normalizedLabel === 'mentalhealthcondition') {
    return 'entity-disease';
  }

  // Symptom/Sign - GREEN
  if (normalizedLabel.includes('symptom') || normalizedLabel.includes('sign') ||
      normalizedLabel === 'physicalcondition') {
    return 'entity-symptom';
  }

  // Medication/Drug - BLUE
  if (normalizedLabel.includes('medication') || normalizedLabel.includes('drug') ||
      normalizedLabel.includes('medicine') || normalizedLabel === 'substance') {
    return 'entity-drug';
  }

  // Dosage - CYAN
  if (normalizedLabel.includes('dosage') || normalizedLabel.includes('dose')) {
    return 'entity-dosage';
  }

  // Treatment/Procedure - PURPLE
  if (normalizedLabel.includes('treatment') || normalizedLabel.includes('procedure') ||
      normalizedLabel.includes('therapeutic') || normalizedLabel.includes('diagnostic') ||
      normalizedLabel === 'treatmenthistory') {
    return 'entity-treatment';
  }

  // Anatomy/Body Structure - YELLOW/AMBER
  if (normalizedLabel.includes('anatomy') || normalizedLabel.includes('biological') ||
      normalizedLabel.includes('structure') || normalizedLabel.includes('bodypart') ||
      normalizedLabel.includes('organ') || normalizedLabel === 'nonbiologicallocation') {
    return 'entity-anatomy';
  }

  // Time/Date/Duration - ORANGE
  if (normalizedLabel.includes('time') || normalizedLabel.includes('date') ||
      normalizedLabel.includes('duration') || normalizedLabel === 'timeexpression') {
    return 'entity-duration';
  }

  // Frequency - LIME
  if (normalizedLabel.includes('frequency')) {
    return 'entity-frequency';
  }

  // History/Clinical Context - GRAY (default)
  if (normalizedLabel.includes('history') || normalizedLabel.includes('familyhistory')) {
    return 'entity-default';
  }

  // Lab Values/Severity/Age/Sex - GRAY (default)
  if (normalizedLabel.includes('lab') || normalizedLabel.includes('value') ||
      normalizedLabel.includes('severity') || normalizedLabel.includes('age') ||
      normalizedLabel.includes('sex') || normalizedLabel.includes('demographic')) {
    return 'entity-default';
  }

  // Clinical Event - GRAY
  if (normalizedLabel.includes('clinical') || normalizedLabel.includes('event')) {
    return 'entity-default';
  }

  // Detailed Description/Area/Distance - GRAY
  if (normalizedLabel.includes('detailed') || normalizedLabel.includes('description') ||
      normalizedLabel.includes('area') || normalizedLabel.includes('distance') ||
      normalizedLabel.includes('riskfactor')) {
    return 'entity-default';
  }

  // Person/Name (for general NER) - BLUE
  if (normalizedLabel.includes('person') || normalizedLabel === 'per' ||
      normalizedLabel === 'name') {
    return 'entity-person';
  }

  // Organization (for general NER) - PURPLE
  if (normalizedLabel.includes('organization') || normalizedLabel === 'org' ||
      normalizedLabel === 'company') {
    return 'entity-organization';
  }

  // Location (for general NER) - GREEN
  if (normalizedLabel.includes('location') || normalizedLabel === 'loc' ||
      normalizedLabel === 'place' || normalizedLabel === 'gpe' ||
      normalizedLabel === 'geo') {
    return 'entity-location';
  }

  // Money/Currency - LIME
  if (normalizedLabel.includes('money') || normalizedLabel.includes('currency')) {
    return 'entity-money';
  }

  // Percent - ORANGE
  if (normalizedLabel.includes('percent')) {
    return 'entity-percent';
  }

  // Default for unknown types
  return 'entity-default';
}
